import { World } from '@ecs/World';
import { CommandController } from '@/input/CommandController';
import { buildFleetSummary, FleetShipSummary } from '@/ui/FleetSummary';

const DOUBLE_CLICK_MS = 350;

/** Bottom HUD listing every ship; click to select, shift-click to add, double-click for the class. */
export class FleetBar {
  private bound = false;
  private lastMarkup = '';
  private lastClick: { id: number; time: number } | null = null;
  private onSelectionChange: () => void = () => {};

  constructor(private readonly world: World, private readonly commands: CommandController) {}

  setSelectionChangeHandler(handler: () => void): void {
    this.onSelectionChange = handler;
  }

  render(): void {
    const container = document.getElementById('fleetBar');
    if (!container) return;
    this.bind(container);

    const ships = buildFleetSummary(this.world, this.commands.getGroups());
    const markup = ships.map(ship => this.card(ship)).join('') +
      `<button class="fleet-all" data-action="all" title="Select all ships (Ctrl+A)">All</button>`;

    if (markup !== this.lastMarkup) {
      container.innerHTML = markup;
      this.lastMarkup = markup;
    }
  }

  private card(ship: FleetShipSummary): string {
    const pips = Array.from({ length: ship.maxHealth }, (_, i) =>
      `<span class="pip${i < ship.health ? ' full' : ''}"></span>`).join('');
    const shieldPct = ship.maxShield > 0 ? Math.round((ship.shield / ship.maxShield) * 100) : 0;
    const groups = ship.groups.map(g => `<span class="group-badge">${g}</span>`).join('');

    return `
      <button class="fleet-card${ship.selected ? ' selected' : ''}" data-id="${ship.id}" style="--ship-color: ${ship.color}">
        <span class="fleet-card-name">${ship.name}${groups}</span>
        <span class="fleet-card-hp">${pips}</span>
        <span class="fleet-card-shield"><span style="width: ${shieldPct}%"></span></span>
      </button>`;
  }

  private bind(container: HTMLElement): void {
    if (this.bound) return;
    this.bound = true;

    // pointerdown rather than click: the markup may be replaced between down and up
    container.addEventListener('pointerdown', (event) => {
      const target = (event.target as HTMLElement).closest<HTMLElement>('[data-id], [data-action]');
      if (!target) return;
      event.preventDefault();
      event.stopPropagation();

      if (target.dataset.action === 'all') {
        this.commands.selectAll();
      } else {
        const id = Number(target.dataset.id);
        const ship = this.world.getEntity(id);
        if (!ship) return;

        const now = Date.now();
        const isDouble = this.lastClick?.id === id && now - this.lastClick.time < DOUBLE_CLICK_MS;
        this.lastClick = { id, time: now };

        if (isDouble) this.commands.selectClassOf(ship);
        else if (event.shiftKey) this.commands.toggle(ship);
        else this.commands.select([ship]);
      }

      this.lastMarkup = '';
      this.render();
      this.onSelectionChange();
    });
  }
}
